'use client';

import React, { memo, useCallback, useMemo, useRef, useState } from 'react';
import { usePermissions } from '@/hooks/use-permissions';

/** Fixed icon + label right rail (grows slightly while magnifying) */
export const QUICK_NAV_W = 72;

const MAG_MAX = 1.55;
const MAG_RANGE = 88; // px influence radius around cursor

interface QuickItem {
  key: string;
  title: string;
  short: string;
  icon: string;
}

const MY_QUICK_OPTIONS: QuickItem[] = [
  { key: 'punch-clock', title: 'Punch Clock', short: 'Punch', icon: 'ri-timer-flash-line' },
  { key: 'attendance', title: 'My Attendance', short: 'Attend', icon: 'ri-calendar-check-line' },
  { key: 'regularization', title: 'Regularization', short: 'Regular', icon: 'ri-edit-line' },
  { key: 'leave-applications', title: 'Apply Leave', short: 'Leave', icon: 'ri-leaf-line' },
  { key: 'leave-balance', title: 'Leave Balance', short: 'Balance', icon: 'ri-pie-chart-line' },
  { key: 'wfh-requests', title: 'WFH Request', short: 'WFH', icon: 'ri-home-4-line' },
  { key: 'employee-self-service', title: 'My Profile', short: 'Profile', icon: 'ri-user-settings-line' },
  { key: 'my-team', title: 'My Team', short: 'Team', icon: 'ri-group-line' },
  { key: 'payslips', title: 'Payslips', short: 'Payslip', icon: 'ri-file-text-line' },
  { key: 'timesheets', title: 'Timesheets', short: 'Timesheet', icon: 'ri-timer-line' },
  { key: 'expenses', title: 'Expenses', short: 'Expense', icon: 'ri-receipt-line' },
  { key: 'holiday-calendar', title: 'Holidays', short: 'Holiday', icon: 'ri-calendar-event-line' },
  { key: 'notifications', title: 'Notifications', short: 'Alerts', icon: 'ri-notification-3-line' },
  { key: 'help-center', title: 'Help Center', short: 'Help', icon: 'ri-question-line' },
];

function dockScale(distancePx: number): number {
  if (distancePx >= MAG_RANGE) return 1;
  // Cosine falloff — smooth dock-like curve
  const t = distancePx / MAG_RANGE;
  return 1 + (MAG_MAX - 1) * Math.cos((t * Math.PI) / 2);
}

interface HRMQuickNavProps {
  currentPage: string;
  onNavigate: (page: string) => void;
}

const HRMQuickNav = memo(function HRMQuickNav({
  currentPage,
  onNavigate,
}: HRMQuickNavProps) {
  const { canView, isAdmin } = usePermissions();
  const navRef = useRef<HTMLElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [cursorY, setCursorY] = useState<number | null>(null);
  const [scales, setScales] = useState<number[]>([]);

  const items = useMemo(
    () =>
      MY_QUICK_OPTIONS.filter(
        item => isAdmin || canView(item.key) || item.key === 'punch-clock',
      ),
    [canView, isAdmin],
  );

  const recomputeScales = useCallback((clientY: number | null) => {
    if (clientY == null) {
      setScales([]);
      setCursorY(null);
      return;
    }
    setCursorY(clientY);
    const next = itemRefs.current.map(el => {
      if (!el) return 1;
      const rect = el.getBoundingClientRect();
      const centerY = rect.top + rect.height / 2;
      return dockScale(Math.abs(clientY - centerY));
    });
    setScales(next);
  }, []);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      recomputeScales(e.clientY);
    },
    [recomputeScales],
  );

  const handleMouseLeave = useCallback(() => {
    recomputeScales(null);
  }, [recomputeScales]);

  const isMagnifying = cursorY != null;

  return (
    <aside
      className={`
        fixed top-0 right-0 h-full z-40 flex flex-col items-center border-l
        transition-[width,box-shadow] duration-200 ease-out
        ${isMagnifying ? 'overflow-visible shadow-[-8px_0_24px_rgba(16,24,40,0.08)]' : 'overflow-hidden'}
      `}
      style={{
        width: isMagnifying ? QUICK_NAV_W + 20 : QUICK_NAV_W,
        backgroundColor: 'var(--hrm-ms-sidebar-bg)',
        borderColor: 'var(--hrm-ms-sidebar-border)',
      }}
      aria-label="My shortcuts"
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      <div
        className="h-16 w-full flex flex-col items-center justify-center gap-0.5 border-b flex-shrink-0"
        style={{ borderColor: 'var(--hrm-ms-sidebar-border)' }}
      >
        <span
          className="w-8 h-8 rounded-lg flex items-center justify-center"
          style={{ backgroundColor: 'var(--hrm-ms-logo-tile)' }}
        >
          <i className="ri-flashlight-fill text-white text-base" />
        </span>
        <span
          className="text-[9px] font-semibold leading-none"
          style={{ color: 'var(--hrm-ms-sidebar-section)' }}
        >
          Quick
        </span>
      </div>

      <nav
        ref={navRef}
        className="relative flex-1 w-full min-h-0 flex flex-col px-1 py-1 gap-0.5"
      >
        {items.map((item, index) => {
          const isActive = currentPage === item.key;
          const scale = scales[index] ?? 1;
          const z = Math.round(scale * 20);

          return (
            <button
              key={item.key}
              ref={el => {
                itemRefs.current[index] = el;
              }}
              type="button"
              onClick={() => onNavigate(item.key)}
              title={item.title}
              aria-label={item.title}
              aria-current={isActive ? 'page' : undefined}
              className={`
                flex-1 min-h-0 w-full flex flex-col items-center justify-center gap-0.5 px-0.5 rounded-lg
                origin-right will-change-transform
                ${isActive
                  ? 'text-[var(--hrm-ms-sidebar-active-text)]'
                  : 'text-[var(--hrm-ms-sidebar-link)]'}
              `}
              style={{
                backgroundColor: isActive
                  ? 'var(--hrm-ms-sidebar-active-bg)'
                  : scale > 1.08
                    ? 'var(--hrm-primary-light)'
                    : undefined,
                transform: `scale(${scale})`,
                zIndex: z,
                transition: isMagnifying
                  ? 'transform 80ms linear, background-color 120ms ease'
                  : 'transform 180ms ease-out, background-color 180ms ease',
              }}
            >
              <i
                className={`${item.icon} leading-none`}
                style={{ fontSize: 'clamp(0.85rem, 1.6vh, 1.15rem)' }}
              />
              <span
                className="font-medium leading-none text-center truncate w-full px-0.5"
                style={{ fontSize: 'clamp(0.5rem, 1.1vh, 0.625rem)' }}
              >
                {item.short}
              </span>
            </button>
          );
        })}
      </nav>
    </aside>
  );
});

export default HRMQuickNav;
